// Pure recommender for sport-artwork templates. Archetype scoring (ARCH) and the
// slot->role matrix (roleFor) are ported verbatim in behavior from
// scripts/sport-templates/gen-sport-templates.cjs (branch claude/busy-northcutt-97ecd2),
// so in-console recommendations match how the 47 sport templates were generated.

export type Role = 'HERO' | 'STATEMENT' | 'MASCOT' | 'YEAR' | 'WORDMARK' | 'ALT';

export type Design = { designTemplateId: string; position: string; label?: string; garmentColor?: string };
export type Style = { styleNumber: string; colors?: Record<string, boolean>; garmentColor?: string; designs?: Design[] };
export type Template = {
  id: string; name?: string; sport?: string; kind?: string; styleCount?: number;
  styles?: Style[]; [k: string]: unknown;
};
export type RecipeLite = { name?: string; defaultPrintArea?: string };

/** A canonical, validator-safe product slot (styleNumber + position already checked against coreGarments). */
export type Slot = { styleNumber: string; category: string; positionCode: string; displayName: string };
export type SlotRec = { slot: Slot; role: Role; recommended: boolean; alreadyHasDesign: boolean; isExactDup: boolean };

export const POS_KEBAB: Record<string, string> = {
  fc: 'full-chest', lc: 'left-chest', ll: 'left-leg', ch: 'center-hat', fb: 'full-back',
};

export const roleLabel: Record<Role, string> = {
  HERO: 'Hero', STATEMENT: 'Statement', MASCOT: 'Emblem', YEAR: 'Heritage', WORDMARK: 'Wordmark', ALT: 'Alt',
};

// Higher = better fit. Ported from the generator's ARCH.
const ARCH: Record<Role, (n: string) => number> = {
  HERO: (n) => (/team \+ logo\b/.test(n) ? 10 : 0) + (/team.*logo/.test(n) ? 4 : 0) + (/team/.test(n) ? 2 : 0),
  STATEMENT: (n) => (/team \+ mascot \+ logo/.test(n) ? 10 : 0) + (/team \+ mascot\b/.test(n) ? 7 : 0) + (/team \+ sport \+ logo/.test(n) ? 6 : 0) + (/team.*mascot/.test(n) ? 3 : 0),
  MASCOT: (n) => (/^.*mascot \+ logo\)?/.test(n) && !/team/.test(n) ? 10 : 0) + (/logo emblem/.test(n) ? 8 : 0) + (/\bmascot\b/.test(n) && !/team/.test(n) ? 6 : 0),
  YEAR: (n) => (/team \+ year \+ logo/.test(n) ? 10 : 0) + (/team \+ year\b/.test(n) ? 9 : 0) + (/year \+ logo/.test(n) ? 7 : 0) + (/mascot \+ year/.test(n) ? 5 : 0) + (/year/.test(n) ? 2 : 0),
  WORDMARK: (n) => (/—\s*team\s*\(#/.test(n) ? 10 : 0) + (/\bteam\b/.test(n) && !/\+/.test(n) ? 9 : 0) + (/team \+ sport\b/.test(n) ? 4 : 0),
  ALT: (n) => (/logo emblem/.test(n) ? 8 : 0) + (/mascot \+ logo/.test(n) ? 7 : 0) + (/\bmascot\b/.test(n) ? 4 : 0),
};
const ORDER: Role[] = ['HERO', 'STATEMENT', 'MASCOT', 'YEAR', 'WORDMARK', 'ALT'];

export function classifyArchetype(recipe: RecipeLite): Role {
  const n = String(recipe.name || '').toLowerCase();
  let best: Role = 'HERO';
  let bs = -1;
  for (const role of ORDER) { const s = ARCH[role](n); if (s > bs) { bs = s; best = role; } }
  if (bs > 0) return best;
  const pa = String(recipe.defaultPrintArea || '').toLowerCase();
  if (pa.includes('left-chest')) return 'MASCOT';
  if (pa.includes('left-leg')) return 'WORDMARK';
  if (pa.includes('hat')) return 'MASCOT';
  return 'HERO';
}

// The roles a (category, positionCode) slot can host — the generator's roleFor
// expanded to a set (tee full-chest cycles through the showcase set by index).
export function rolesForSlot(category: string, positionCode: string): Role[] {
  if (positionCode === 'lc') return category === 'tees' ? ['YEAR'] : ['MASCOT'];
  if (positionCode === 'ch' || category === 'hats') return ['MASCOT'];
  if (positionCode === 'll' || category === 'joggers') return ['WORDMARK'];
  if (category === 'bags') return ['MASCOT'];
  if (positionCode === 'fc') {
    if (category === 'hoodies' || category === 'crewnecks') return ['HERO'];
    if (category === 'tees') return ['STATEMENT', 'HERO', 'WORDMARK', 'ALT'];
    return ['HERO'];
  }
  return ['HERO'];
}

export function recommendSlots(recipe: RecipeLite & { id: string }, slots: Slot[], existingStyles: Style[]): SlotRec[] {
  const archetype = classifyArchetype(recipe);
  const byStyle = new Map<string, Set<string>>();
  for (const s of existingStyles) {
    byStyle.set(String(s.styleNumber).toUpperCase(), new Set((s.designs || []).map((d) => `${d.designTemplateId}|${d.position}`)));
  }
  return slots.map((slot) => {
    const roles = rolesForSlot(slot.category, slot.positionCode);
    const kebab = POS_KEBAB[slot.positionCode] || slot.positionCode;
    const set = byStyle.get(String(slot.styleNumber).toUpperCase());
    const isExactDup = !!set && set.has(`${recipe.id}|${kebab}`);
    const alreadyHasDesign = !!set && [...set].some((k) => k.endsWith(`|${kebab}`));
    return { slot, role: archetype, recommended: roles.includes(archetype), alreadyHasDesign, isExactDup };
  });
}

/** Add the chosen recipe to each checked slot. Pure; returns a new template. */
export function applyDesignsToTemplate(
  template: Template,
  recipeId: string,
  additions: { slot: Slot; role: Role }[],
  productTemplate: Template | null,
): Template {
  const t: Template = JSON.parse(JSON.stringify(template));
  t.styles = t.styles || [];
  const findStyle = (sn: string) => t.styles!.find((s) => String(s.styleNumber).toUpperCase() === sn.toUpperCase());
  const colorsFor = (sn: string) =>
    (productTemplate?.styles || []).find((s) => String(s.styleNumber).toUpperCase() === sn.toUpperCase())?.colors || {};
  for (const a of additions) {
    const sn = a.slot.styleNumber;
    const kebab = POS_KEBAB[a.slot.positionCode] || a.slot.positionCode;
    const label = `${roleLabel[a.role]} · ${a.slot.positionCode.toUpperCase()}`;
    let style = findStyle(sn);
    if (!style) { style = { styleNumber: sn, colors: colorsFor(sn), designs: [] }; t.styles.push(style); }
    style.designs = style.designs || [];
    if (!style.designs.some((d) => d.designTemplateId === recipeId && d.position === kebab)) {
      style.designs.push({ designTemplateId: recipeId, position: kebab, label });
    }
  }
  t.styleCount = t.styles.length;
  return t;
}

/** Remove every placement of a design; drop styles left with no designs. Pure. */
export function removeDesignFromTemplate(template: Template, designTemplateId: string): Template {
  const t: Template = JSON.parse(JSON.stringify(template));
  t.styles = (t.styles || [])
    .map((s) => ({ ...s, designs: (s.designs || []).filter((d) => d.designTemplateId !== designTemplateId) }))
    .filter((s) => (s.designs || []).length > 0);
  t.styleCount = t.styles.length;
  return t;
}
